import { Link } from "react-router-dom";
import { Shield, Lock, FileCheck, Mail, Heart, ArrowUp, Users } from "lucide-react";
import { FairStaysLogo } from "@/components/ui/fairstays-logo";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/useAuth";
import { useProfiles } from "@/hooks/useProfiles";
import { useCooperativeMembership } from "@/hooks/useCooperativeMembership";
import { useRealtimeSync } from "@/hooks/useRealtimeSync";
import { useState, useEffect } from "react";
import { BecomeHostDialog } from "./cooperative/BecomeHostDialog";
import { CooperativeMemberDialog } from "./cooperative/CooperativeMemberDialog";

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const { user } = useAuth();
  const { profile, fetchProfile } = useProfiles();
  const { membership, fetchMembership } = useCooperativeMembership();
  const [showBecomeHostDialog, setShowBecomeHostDialog] = useState(false);
  const [showCooperativeDialog, setShowCooperativeDialog] = useState(false);
  
  // Listen to realtime updates for profile and membership changes
  const { lastUpdate } = useRealtimeSync('all');

  // Refetch data when realtime updates occur
  useEffect(() => {
    if (lastUpdate && user) {
      fetchProfile(user.id);
      fetchMembership();
    }
  }, [lastUpdate, user, fetchProfile, fetchMembership]);

  // Listen for custom event to open become host dialog
  useEffect(() => {
    const handleOpenBecomeHostDialog = () => {
      setShowBecomeHostDialog(true);
    };

    window.addEventListener('open-become-host-dialog', handleOpenBecomeHostDialog);
    return () => {
      window.removeEventListener('open-become-host-dialog', handleOpenBecomeHostDialog);
    };
  }, []);

  const isCooperativeMember = membership?.status === 'approved';
  const isHost = profile?.host_approved || false;

  const getFooterCTA = () => {
    if (!user || !profile) {
      return {
        text: 'Gastgeber werden',
        onClick: () => setShowBecomeHostDialog(true),
        show: true
      }
    }

    if (isCooperativeMember) {
      return {
        text: 'Du bist bereits Genossenschaftsmitglied',
        onClick: undefined,
        show: true
      }
    }

    if (isHost) {
      return {
        text: 'Genossenschaftsmitglied werden',
        onClick: () => setShowCooperativeDialog(true),
        show: true
      }
    }

    return {
      text: 'Gastgeber werden',
      onClick: () => setShowBecomeHostDialog(true),
      show: true
    }
  };

  const footerCTA = getFooterCTA();

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleLinkClick = () => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  };
  const legalLinks = [
    { name: "Impressum", href: "/impressum", icon: <FileCheck className="w-3 h-3" /> },
    { name: "Datenschutz", href: "/privacy", icon: <Shield className="w-3 h-3" /> },
    { name: "AGB", href: "/terms", icon: <FileCheck className="w-3 h-3" /> },
    { name: "Cookies", href: "/cookies", icon: <FileCheck className="w-3 h-3" /> },
    { name: "Sicherheit", href: "/security", icon: <Lock className="w-3 h-3" /> }
  ];

  const contactLinks = [
    { name: "Kontakt", href: "/contact", icon: <Mail className="w-3 h-3" /> },
    { name: "Hilfe", href: "/help", icon: <Heart className="w-3 h-3" /> },
    { name: "Genossenschaft", href: "/cooperative", icon: <Users className="w-3 h-3" /> }
  ];

  return (
    <>
      <BecomeHostDialog isOpen={showBecomeHostDialog} onClose={() => setShowBecomeHostDialog(false)} />
      <CooperativeMemberDialog isOpen={showCooperativeDialog} onClose={() => setShowCooperativeDialog(false)} />
      
      <footer className="bg-gradient-to-t from-muted/50 to-background border-t">
        <div className="container mx-auto px-4 py-12">
          {/* Footer CTA Bar */}
          {footerCTA.show && (
            <div className="mb-8 p-4 bg-muted/50 rounded-lg border border-border">
              <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                <div className="text-center sm:text-left">
                  <p className="text-sm font-medium">
                    {footerCTA.text}
                  </p>
                </div>
                {footerCTA.onClick && (
                  <Button 
                    onClick={footerCTA.onClick}
                    variant="default"
                    size="sm"
                    className="w-full sm:w-auto"
                  >
                    {isCooperativeMember ? 'Mitglied' : 'Mehr erfahren'}
                  </Button>
                )}
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          {/* Brand Section */}
          <div className="md:col-span-1">
            <Link to="/" className="flex items-center space-x-2 mb-4">
              <FairStaysLogo className="h-6 w-6" />
              <span className="text-xl font-bold text-primary">Fair-Stays</span>
            </Link>
            <p className="text-sm text-muted-foreground mb-4">
              Ihre Plattform für Unterkünfte. 
              Privatsphäre und Sicherheit stehen an erster Stelle.
            </p>
          </div>

          {/* Legal Links */}
          <div>
            <h3 className="font-semibold mb-4 flex items-center gap-2">
              <FileCheck className="w-4 h-4" />
              Rechtliches
            </h3>
            <ul className="space-y-2">
              {legalLinks.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.href}
                    onClick={handleLinkClick}
                    className="text-sm text-muted-foreground hover:text-foreground transition-colors flex items-center gap-2"
                  >
                    {link.icon}
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Links */}
          <div>
            <h3 className="font-semibold mb-4 flex items-center gap-2">
              <Mail className="w-4 h-4" />
              Kontakt & Support
            </h3>
            <ul className="space-y-2">
              {contactLinks.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.href}
                    onClick={handleLinkClick}
                    className="text-sm text-muted-foreground hover:text-foreground transition-colors flex items-center gap-2"
                  >
                    {link.icon}
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
            
            {/* Direct Contact Info */}
            <div className="mt-4 pt-4 border-t border-border/50">
              <p className="text-xs text-muted-foreground mb-1">
                📧 Lucas.lippe@gmx.de
              </p>
              <p className="text-xs text-muted-foreground">
                Antwort innerhalb von 24h
              </p>
            </div>
          </div>

          {/* Company Info */}
          <div>
            <h3 className="font-semibold mb-4">Anbieter</h3>
            <div className="text-sm text-muted-foreground space-y-1">
              <p className="font-medium">Cordt Lucas Lippe</p>
              <p>Korvin 3a</p>
              <p>29459 Clenze</p>
              <p>Deutschland</p>
            </div>
            
            <div className="mt-4 pt-4 border-t border-border/50 text-xs text-muted-foreground">
              <p>Einzelunternehmen</p>
              <p className="mt-1">
                © Fair-Stays
              </p>
            </div>
          </div>
        </div>

        {/* Bottom Section */}
        <div className="border-t border-border/50 mt-8 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="text-sm text-muted-foreground">
              © {currentYear} Fair-Stays. Alle Rechte vorbehalten.
            </div>
            
            {/* Scroll to Top Button - Mobile only */}
            <Button
              onClick={scrollToTop}
              variant="outline"
              size="sm"
              className="flex items-center gap-2 md:hidden"
            >
              <ArrowUp className="w-4 h-4" />
              Nach oben
            </Button>
            
            {/* Security Notice */}
            <div className="flex flex-wrap items-center justify-center gap-4 text-xs text-muted-foreground">
              <div className="flex items-center gap-1">
                <Lock className="w-3 h-3 text-green-600" />
                <span>Verschlüsselte Verbindung</span>
              </div>
              <div className="flex items-center gap-1">
                <Shield className="w-3 h-3 text-blue-600" />
                <span>DSGVO-konform</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
    </>
  );
};

export default Footer;